import Helpers from "./helpers";
import Nav from "./nav";

enum ScrollDirection {
	Up,
	Down,
}

enum HeaderState {
	NonSticky,
	StickyHidden,
	StickyVisible,
	SubnavOpenWhenScrolling,
	Transitioning
}

export default class Header {

	private lastScrollTop = 0;
	private headerElement = document.querySelector('.c-header-wrapper') as HTMLElement ? document.querySelector('.c-header-wrapper') as HTMLElement : document.querySelector('.c-header') as HTMLElement;
	private lastDirection: ScrollDirection = ScrollDirection.Down;
	private navDimmer = document.querySelector('.js-mega-nav-dimmer') as HTMLDivElement;
	private navOpenedBeforePotentiallySticky = false;
	private headerHeight = this.headerElement.clientHeight;
	private headerState = HeaderState.NonSticky;
	private previousHeaderState = null;
	private isResettingHeader = false;
	private readonly scrollingDownCssClass = 'is-scrolling-down';
	private hasViewportBannerBehindHeader = document.documentElement.classList.contains('has-viewport-banner-behind-header');
	private headerTop = document.querySelector('.js-header-top') ? document.querySelector('.js-header-top').clientHeight : 0;
	private transitionBuffer = 20 + this.headerTop;
	private isTransitioning = false;
	private currentTranslateY: number = 0;
	private lastScrollEventTime: number = Date.now();

	constructor() {
		if (document.querySelector('.js-header-sticky')) {
			this.attachScrollEvent();
			this.attachNavEvents();
			this.startHeaderChecker();
		}
		if (this.hasViewportBannerBehindHeader) {
			this.setHeaderHeightCssVariable();
			this.attachHeaderResizeObserver();
		}
	}

	private attachHeaderResizeObserver() {
		const resizeObserver = new ResizeObserver(() => {
			this.setHeaderHeightCssVariable();
		});
		resizeObserver.observe(this.headerElement);
	}

	private shouldBeSticky() {
		if (matchMedia('(min-width: 980px)').matches && matchMedia('(max-height: 900px)').matches) {
			return false;
		}
		if (this.navOpenedBeforePotentiallySticky) {
			return false;
		}
		return true;
	}

	private startHeaderChecker() {
		setInterval(() => {
			this.headerChecker();
		}, 150);
	}

	private attachScrollEvent() {
		window.addEventListener('scroll', this.scrollCheck.bind(this), { passive: true });
	}

	private attachNavEvents() {
		document.addEventListener('navDimmerActive', () => {
			if ((window.pageYOffset || document.documentElement.scrollTop) < this.headerHeight) {
				this.navOpenedBeforePotentiallySticky = true;
			}
		}, false);

		document.addEventListener('navDimmerClose', () => {
			this.navOpenedBeforePotentiallySticky = false;
		}, false);

		document.addEventListener('resetHeader', () => {
			this.resetHeader();
		}, false);
	}

	private headerChecker() {

		if (this.headerState === this.previousHeaderState && !this.isTransitioning) {
			return;
		}

		if (!this.shouldBeSticky()) {
			this.resetHeader();
			return;
		}

		requestAnimationFrame(() => {

			switch (this.headerState) {
				case HeaderState.NonSticky:
					this.resetHeader();
					break;
				case HeaderState.StickyHidden:
					this.handleStickyHidden();
					break;
				case HeaderState.StickyVisible:
					this.handleStickyVisible();
					break;
				case HeaderState.SubnavOpenWhenScrolling:
					this.handleSubnavOpen();
					break;
				case HeaderState.Transitioning:
					this.handleTransition();
					break;
			}

			this.previousHeaderState = this.headerState;
		});
	}

	private handleStickyHidden() {
		if (this.previousHeaderState === HeaderState.NonSticky) {
			this.headerElement.classList.add('no-transition');
		}
		this.headerElement.classList.add('is-sticky');
		this.headerElement.style.transform = 'translateY(-100%)';
		document.body.classList.add('header-is-sticky');
		document.body.classList.add(this.scrollingDownCssClass);
		this.headerElement.classList.add(this.scrollingDownCssClass);
		
		if (Nav && this.lastDirection !== ScrollDirection.Down && !document.documentElement.classList.contains('html--nav-open')) {
			const triggerNavResetEvent = Helpers.createCustomEventForDispatch('resetNav');
			document.dispatchEvent(triggerNavResetEvent);
		}
	}

	private handleStickyVisible() {
		this.headerElement.classList.remove('no-transition');
		this.headerElement.classList.add('is-sticky');
		document.body.classList.add('header-is-sticky');
		this.updateTranslateY();
		this.headerElement.style.transform = `translateY(${this.currentTranslateY}px)`;
		document.body.classList.remove(this.scrollingDownCssClass);
		this.headerElement.classList.remove(this.scrollingDownCssClass);
	}

	private handleSubnavOpen() {
		document.body.classList.remove(this.scrollingDownCssClass);
		this.headerElement.classList.remove(this.scrollingDownCssClass);
	}

	private handleTransition() {
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const progress = this.calculateTransitionProgress(scrollTop);
		// If the user stopped scrolling upwards for >1s and we're not at 0 yet, snap the header fully into view (translateY = 0).
		const idleForMs = Date.now() - this.lastScrollEventTime;
		if (
			this.lastDirection === ScrollDirection.Up &&
			idleForMs > 1000 &&
			this.currentTranslateY !== 0
		) {
			this.currentTranslateY = 0;
			this.headerElement.style.transform = 'translateY(0px)';
		}
		
		// Smooth transition between sticky and non-sticky
		if (this.previousHeaderState === HeaderState.StickyVisible && this.lastDirection === ScrollDirection.Up) {
			this.updateTranslateY(progress);
			this.headerElement.style.transform = `translateY(${this.currentTranslateY}px)`;
			
			// Complete transition when close enough to top
			if (progress <= 0.1) {
				this.forceTransitionComplete();
			}
		}
	}

	private updateTranslateY(normalizedProgress?: number): void {
		const headerOffsetPixels = this.headerOffset();
		let translateY = typeof normalizedProgress === 'number' ? -headerOffsetPixels * normalizedProgress : -headerOffsetPixels;
		translateY = Math.min(0, Math.max(-headerOffsetPixels, translateY));
		this.currentTranslateY = translateY;
	}

	private calculateTransitionProgress(scrollTop: number): number {
		const transitionZone = this.headerHeight + this.transitionBuffer;
		if (scrollTop >= transitionZone) return 1;
		if (scrollTop <= this.transitionBuffer) return 0;
		return (scrollTop - this.transitionBuffer) / this.headerHeight;
	}

	private scrollCheck() {
		// Record the time of this scroll event for idle detection logic
		this.lastScrollEventTime = Date.now();
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const headerHeight = this.headerHeight;

		if (this.isResettingHeader) {
			this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
			return;
		}

		// Determine scroll direction with small threshold to prevent jitter
		const scrollDelta = scrollTop - this.lastScrollTop;
		const scrollThreshold = 3;

		if (Math.abs(scrollDelta) > scrollThreshold) {
			this.lastDirection = scrollDelta > 0 ? ScrollDirection.Down : ScrollDirection.Up;
		}

		// if subnav open when scrolling
		if (this.subnavIsOpen(scrollTop)) {
			this.headerState = HeaderState.SubnavOpenWhenScrolling;
			this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
			return;
		}

		if (this.lastDirection === ScrollDirection.Down) {
			// Scrolling down
			if (scrollTop > headerHeight + this.transitionBuffer) {
				if (this.headerState !== HeaderState.StickyHidden) {
					this.headerState = HeaderState.StickyHidden;
				}
			}
		} else {
			// Scrolling up
			if (scrollTop <= 5) {
				// Skip force-complete if search is open
				if (this.searchIsOpen()) {
				} else if (this.headerState !== HeaderState.NonSticky) {
					this.forceTransitionComplete();
					this.cleanupHeaderDOM();
				}
			} else if (scrollTop < this.transitionBuffer) {
				// Near the very top - go back to non-sticky with transition
				if (this.headerState === HeaderState.StickyVisible) {
					this.headerState = HeaderState.Transitioning;
					this.isTransitioning = true;
				} else if (this.headerState !== HeaderState.NonSticky && !this.isTransitioning) {
					this.headerState = HeaderState.NonSticky;
				}
			} else if (scrollTop >= headerHeight - this.transitionBuffer && this.headerState !== HeaderState.StickyVisible) {
				// In the middle zone - show sticky header
				this.headerState = HeaderState.StickyVisible;
				this.isTransitioning = false;
			} else if (scrollTop < headerHeight - this.transitionBuffer && this.headerState === HeaderState.StickyVisible) {
				// Start transitioning when leaving the sticky zone
				this.headerState = HeaderState.Transitioning;
				this.isTransitioning = true;
			}
		}
		this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
	}

	private subnavIsOpen(scrollTop: number) {
		return this.navDimmer.classList.contains('js-mega-nav-dimmer--show')
			&& (scrollTop > this.lastScrollTop)
			&& document.body.classList.contains('header-is-sticky');
	}

	private searchIsOpen(): boolean {
		return document.querySelector('.js-mega-nav-dimmer.js-mega-nav-dimmer--search.js-mega-nav-dimmer--show') !== null;
	}

	private cleanupHeaderDOM() {
		this.headerElement.classList.remove('is-sticky', 'no-transition', this.scrollingDownCssClass);
		document.body.classList.remove('header-is-sticky', this.scrollingDownCssClass);
		this.headerElement.removeAttribute('style');
	}

	private forceTransitionComplete() {
		this.headerState = HeaderState.NonSticky;
		this.isTransitioning = false;
		this.headerElement.style.transform = '';
	}
	
	private resetHeader() {
		this.isResettingHeader = true;
		this.isTransitioning = false;
	
		setTimeout(() => {
			this.isResettingHeader = false;
		}, 400);
	
		this.cleanupHeaderDOM();
	}

	private headerOffset() {
		const headerTop = document.querySelector('.js-header-top'); // Tap has a top header
		if (headerTop) {
			return headerTop.clientHeight;
		}
		return 0; // Drench and OR have no top header
	}

	private setHeaderHeightCssVariable() {
		const headerHeight = this.headerElement.clientHeight + 2; // adding for any potential rounding errors
		document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
	}
}
